import { useToolOwnership } from "./call/shared/toolOwnership"
import type { Dispatch, SetStateAction } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ArrowLeft, MessageSquare, RefreshCw, Send, UserRound } from "lucide-react"

import { Button } from "components/ui/button"
import { Textarea } from "components/ui/textarea"
import { linkifyText } from "utils/linkifyText"
import { meetings } from "services/api/meetingsClient"
import type {
  MeetingDirectMessage,
  MeetingDirectMessageParticipant,
  MeetingDirectMessageThread,
} from "services/api/meetingsClient"
import { writeMeetingMessagesActiveThread } from "./meetingMessagesOpenCommand"
import { useMeetingPanelEvents } from "./useMeetingPanelEvents"

const PANEL_POLL_INTERVAL_MS = 60000
const THREAD_POLL_INTERVAL_MS = 30000
const TRANSIENT_ERROR_BACKOFF_MS = 60 * 1000
const RECIPIENT_PRESENCE_GRACE_MS = 2 * 60 * 1000

type CachedRecipient = {
  recipient: MeetingDirectMessageParticipant
  lastObservedAt: number
}

const formatMessageTime = (iso?: string | null) => {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
}

const sortThreads = (threads: MeetingDirectMessageThread[]) =>
  [...threads].sort((left, right) => new Date(right.updated_at || 0).getTime() - new Date(left.updated_at || 0).getTime())

const upsertThread = (threads: MeetingDirectMessageThread[], nextThread: MeetingDirectMessageThread) =>
  sortThreads([nextThread, ...threads.filter((thread) => thread.id !== nextThread.id)])

const isTransientPanelError = (err: any) => (
  err?.code === "ECONNABORTED" ||
  /timeout/i.test(String(err?.message || "")) ||
  String(err?.message || "") === "Network Error" ||
  !err?.response
)

/** A request to open a thread or a recipient; `key` changes on every request so the same target can be reopened. */
type MessagesOpenCommand = { threadId?: string; recipientIdentityKey?: string; createdAt: number }

const getOpenCommandKey = (command: MessagesOpenCommand) =>
  `${command.createdAt}:${command.threadId || ""}:${command.recipientIdentityKey || ""}`

export type MessageDrafts = Record<string, string>
export type MessageDraftState = {
  /** The call owns pending sends even while its Tools panel is closed. */
  captureOwner?: () => () => boolean
  drafts: MessageDrafts
  setDrafts: Dispatch<SetStateAction<MessageDrafts>>
  sendingThreads: Record<string, boolean>
  setSendingThreads: Dispatch<SetStateAction<Record<string, boolean>>>
}

/** The direct-messages panel lives inside the WiseFriend call; the surface hands it its context and open targets. */
export type MessagesEmbedContext = {
  ownershipKey?: string
  meetingId: string
  panelToken: string
  /** Call-owned memory preserves drafts when the Tools workspace unmounts. */
  draftState?: MessageDraftState
  /** Hidden panels retain drafts and cached messages, but must never mark them read. */
  visible: boolean
  /** A new key opens the given thread or recipient; a new key without either returns to the thread list. */
  openTarget?: { key: number; threadId?: string; recipientIdentityKey?: string } | null
  /** Called after a message is stored, so the call can tell the recipient over Daily as well. */
  onMessageSent?: (info: { threadId: string; recipientIdentityKey: string; senderIdentityKey: string }) => void
}

function MeetingMessagesSession({ embed }: { embed: MessagesEmbedContext }) {
  const { meetingId, panelToken, visible } = embed
  const ownership = useToolOwnership(`${meetingId}:${embed.ownershipKey ?? panelToken}`)
  const captureTool = ownership.capture
  const visibleRef = useRef(visible)
  visibleRef.current = visible
  useEffect(() => {
    visibleRef.current = visible
    return () => { visibleRef.current = false }
  }, [visible])
  const openThreadId = embed.openTarget?.threadId || ""
  const openRecipientIdentityKey = embed.openTarget?.recipientIdentityKey || ""
  const openTargetKey = embed.openTarget?.key ?? 0
  const [recipients, setRecipients] = useState<MeetingDirectMessageParticipant[]>([])
  const [threads, setThreads] = useState<MeetingDirectMessageThread[]>([])
  const [identityKey, setIdentityKey] = useState("")
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
  const [messages, setMessages] = useState<MeetingDirectMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [localDrafts, setLocalDrafts] = useState<MessageDrafts>({})
  const { drafts, setDrafts } = embed.draftState || { drafts: localDrafts, setDrafts: setLocalDrafts }
  const body = selectedThreadId ? drafts[selectedThreadId] || "" : ""
  const [localSendingThreads, setLocalSendingThreads] = useState<Record<string, boolean>>({})
  const { sendingThreads, setSendingThreads } = embed.draftState || { sendingThreads: localSendingThreads, setSendingThreads: setLocalSendingThreads }
  const sending = Boolean(selectedThreadId && sendingThreads[selectedThreadId])
  const [openingIdentityKey, setOpeningIdentityKey] = useState<string | null>(null)
  const [pendingOpenCommand, setPendingOpenCommand] = useState<MessagesOpenCommand | null>(null)
  const [error, setError] = useState("")
  const [realtimeUrl, setRealtimeUrl] = useState<string | null>(null)
  const hasLoadedPanelRef = useRef(false)
  const selectedThreadIdRef = useRef<string | null>(null)
  const messagesByThreadIdRef = useRef<Record<string, MeetingDirectMessage[]>>({})
  const recipientCacheRef = useRef<Record<string, CachedRecipient>>({})
  const panelRetryAfterRef = useRef(0)
  const threadRetryAfterRef = useRef<Record<string, number>>({})
  const loadPanelInFlightRef = useRef(false)
  const loadThreadInFlightRef = useRef<Record<string, boolean>>({})
  const pendingPanelRefreshRef = useRef(false)
  const pendingThreadRefreshRef = useRef<Record<string, boolean>>({})
  const openCommandRefreshKeyRef = useRef("")

  const selectedThread = useMemo(
    () => threads.find((thread) => thread.id === selectedThreadId) || null,
    [selectedThreadId, threads],
  )

  selectedThreadIdRef.current = selectedThreadId

  useEffect(() => {
    if (!meetingId || !panelToken) return
    if (visible && selectedThread) {
      writeMeetingMessagesActiveThread(meetingId, panelToken, {
        id: selectedThread.id,
        recipientIdentityKey: selectedThread.recipient.identity_key,
      })
    } else {
      writeMeetingMessagesActiveThread(meetingId, panelToken, null)
    }
    return () => {
      writeMeetingMessagesActiveThread(meetingId, panelToken, null)
    }
  }, [meetingId, panelToken, selectedThread, visible])

  const threadByRecipientKey = useMemo(() => {
    const map = new Map<string, MeetingDirectMessageThread>()
    threads.forEach((thread) => {
      map.set(thread.recipient.identity_key, thread)
    })
    return map
  }, [threads])

  const recipientRows = useMemo(
    () => recipients.map((recipient) => ({
      recipient,
      thread: threadByRecipientKey.get(recipient.identity_key) || null,
    })),
    [recipients, threadByRecipientKey],
  )

  const markThreadRead = useCallback(async (threadId: string) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!meetingId || !panelToken || !threadId || !visibleRef.current || selectedThreadIdRef.current !== threadId) return
    try {
      const thread = await meetings.markMeetingDirectMessageThreadRead(meetingId, panelToken, threadId)
      if (!ownsTool()) return;
      setThreads((prev) => upsertThread(prev, thread))
    } catch {
      if (!ownsTool()) return;
      // Message loading should remain usable even if read-state sync fails briefly.
    }
  }, [meetingId, panelToken, captureTool])

  useEffect(() => {
    if (!meetingId || !openTargetKey) return
    if (!openThreadId && !openRecipientIdentityKey) {
      // Opened from the Messages button: always land on the list, not on whatever thread was left open.
      setPendingOpenCommand(null)
      setSelectedThreadId(null)
      return
    }
    setPendingOpenCommand({
      threadId: openThreadId || undefined,
      recipientIdentityKey: openRecipientIdentityKey || undefined,
      createdAt: Date.now(),
    })
  }, [meetingId, openRecipientIdentityKey, openTargetKey, openThreadId])

  const mergeRecentlyVisibleRecipients = useCallback((
    nextRecipients: MeetingDirectMessageParticipant[],
    nextThreads: MeetingDirectMessageThread[],
    currentIdentityKey: string,
  ) => {
    const now = Date.now()
    const nextRecipientKeys = new Set(nextRecipients.map((recipient) => recipient.identity_key))
    const threadRecipientKeys = new Set(nextThreads.map((thread) => thread.recipient.identity_key))
    const nextCache = { ...recipientCacheRef.current }

    nextRecipients.forEach((recipient) => {
      nextCache[recipient.identity_key] = {
        recipient,
        lastObservedAt: now,
      }
    })

    Object.keys(nextCache).forEach((identityKey) => {
      const cached = nextCache[identityKey]
      const stillGraceful = now - cached.lastObservedAt <= RECIPIENT_PRESENCE_GRACE_MS
      if (!threadRecipientKeys.has(identityKey) || !stillGraceful || identityKey === currentIdentityKey) {
        delete nextCache[identityKey]
      }
    })
    recipientCacheRef.current = nextCache

    const retainedRecipients = nextThreads
      .map((thread) => nextCache[thread.recipient.identity_key]?.recipient)
      .filter((recipient): recipient is MeetingDirectMessageParticipant => (
        Boolean(recipient) &&
        !nextRecipientKeys.has(recipient.identity_key) &&
        recipient.identity_key !== currentIdentityKey
      ))

    return [...nextRecipients, ...retainedRecipients]
  }, [])

  const loadPanel = useCallback(async (options: { silent?: boolean; force?: boolean } = {}) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!meetingId || !panelToken) {
      setError("Messages are unavailable.")
      setLoading(false)
      return
    }
    if (loadPanelInFlightRef.current) {
      if (options.silent) pendingPanelRefreshRef.current = true
      setLoading(false)
      return
    }
    if (!options.force && Date.now() < panelRetryAfterRef.current) {
      setLoading(false)
      return
    }
    loadPanelInFlightRef.current = true
    try {
      const response = await meetings.getMeetingMessagesPanel(meetingId, panelToken)
      if (!ownsTool()) return;
      const nextThreads = sortThreads(response.threads || [])
      setIdentityKey(response.identity_key)
      setRealtimeUrl(response.realtime_url || null)
      setRecipients(mergeRecentlyVisibleRecipients(response.recipients || [], nextThreads, response.identity_key))
      setThreads(nextThreads)
      hasLoadedPanelRef.current = true
      panelRetryAfterRef.current = 0
      setError("")
    } catch (err: any) {
      if (!ownsTool()) return;
      if (isTransientPanelError(err)) {
        panelRetryAfterRef.current = Date.now() + TRANSIENT_ERROR_BACKOFF_MS
      }
      if (!options.silent || !hasLoadedPanelRef.current) {
        setError(err?.response?.data?.detail || err?.message || "Unable to load messages.")
      }
    } finally {
      if (!ownsTool()) return;
      loadPanelInFlightRef.current = false
      setLoading(false)
      const shouldRunPendingRefresh = pendingPanelRefreshRef.current && Date.now() >= panelRetryAfterRef.current
      pendingPanelRefreshRef.current = false
      if (shouldRunPendingRefresh) {
        window.setTimeout(() => {
          void loadPanel({ silent: true })
        }, 0)
      }
    }
  }, [meetingId, mergeRecentlyVisibleRecipients, panelToken, captureTool])

  const loadThreadMessages = useCallback(async (threadId: string, options: { silent?: boolean; force?: boolean } = {}) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!meetingId || !panelToken || !threadId) return
    if (loadThreadInFlightRef.current[threadId]) {
      if (options.silent) pendingThreadRefreshRef.current[threadId] = true
      return
    }
    if (!options.force && Date.now() < (threadRetryAfterRef.current[threadId] || 0)) return
    loadThreadInFlightRef.current[threadId] = true
    if (!options.silent) setMessagesLoading(true)
    try {
      const response = await meetings.getMeetingDirectMessageThreadMessages(meetingId, panelToken, threadId)
      if (!ownsTool()) return;
      const nextMessages = response.messages || []
      messagesByThreadIdRef.current = {
        ...messagesByThreadIdRef.current,
        [threadId]: nextMessages,
      }
      if (selectedThreadIdRef.current === threadId) {
        setMessages(nextMessages)
      }
      setThreads((prev) => upsertThread(prev, response.thread))
      threadRetryAfterRef.current[threadId] = 0
      if ((response.thread.unread_count || 0) > 0) {
        await markThreadRead(response.thread.id)
      if (!ownsTool()) return;
      }
      setError("")
    } catch (err: any) {
      if (!ownsTool()) return;
      if (isTransientPanelError(err)) {
        threadRetryAfterRef.current[threadId] = Date.now() + TRANSIENT_ERROR_BACKOFF_MS
      }
      if (!options.silent) {
        setError(err?.response?.data?.detail || err?.message || "Unable to load this conversation.")
      }
    } finally {
      if (!ownsTool()) return;
      loadThreadInFlightRef.current[threadId] = false
      if (!options.silent) setMessagesLoading(false)
      const shouldRunPendingRefresh = pendingThreadRefreshRef.current[threadId] &&
        Date.now() >= (threadRetryAfterRef.current[threadId] || 0)
      pendingThreadRefreshRef.current[threadId] = false
      if (shouldRunPendingRefresh) {
        window.setTimeout(() => {
          void loadThreadMessages(threadId, { silent: true })
        }, 0)
      }
    }
  }, [markThreadRead, meetingId, panelToken, captureTool])

  useEffect(() => {
    loadPanel()
    const intervalId = window.setInterval(() => loadPanel({ silent: true }), PANEL_POLL_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [loadPanel])

  useEffect(() => {
    if (!selectedThreadId || !visible) {
      setMessagesLoading(false)
      return
    }
    const cachedMessages = messagesByThreadIdRef.current[selectedThreadId]
    if (cachedMessages) {
      setMessages(cachedMessages)
      loadThreadMessages(selectedThreadId, { silent: true })
    } else {
      setMessages([])
      loadThreadMessages(selectedThreadId)
    }
    const intervalId = window.setInterval(() => loadThreadMessages(selectedThreadId, { silent: true }), THREAD_POLL_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [loadThreadMessages, selectedThreadId, visible])

  // A send can finish while the workspace is unmounted. If it reopened while
  // pending, refresh this new panel instance when the call-owned request settles.
  const inheritedSendingThreadsRef = useRef(new Set(Object.keys(sendingThreads)))
  useEffect(() => {
    if (!sending && visible && selectedThreadId && inheritedSendingThreadsRef.current.has(selectedThreadId)) {
      inheritedSendingThreadsRef.current.delete(selectedThreadId)
      void loadThreadMessages(selectedThreadId, { silent: true, force: true })
    }
  }, [loadThreadMessages, selectedThreadId, sending, visible])

  useMeetingPanelEvents({ onReconnect: () => { void loadPanel({ silent: true, force: true }); if (selectedThreadIdRef.current) void loadThreadMessages(selectedThreadIdRef.current, { silent: true, force: true }) }, panelToken,
    realtimeUrl,
    enabled: Boolean(meetingId && panelToken),
    onEvent: (event) => {
      if (event.type === "direct_message.presence_changed") {
        loadPanel({ silent: true })
        return
      }
      if (event.type === "direct_message.created" || event.type === "direct_message.read") {
        const threadId = typeof event.thread_id === "string" ? event.thread_id : ""
        const senderIdentityKey = typeof event.sender_identity_key === "string" ? event.sender_identity_key : ""
        if (event.type === "direct_message.created" && senderIdentityKey && senderIdentityKey === identityKey) {
          return
        }
        if (threadId && threadId === selectedThreadIdRef.current) {
          loadThreadMessages(threadId, { silent: true })
        } else {
          loadPanel({ silent: true })
        }
      }
    },
  })

  const refreshPanel = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (refreshing) return
    setRefreshing(true)
    panelRetryAfterRef.current = 0
    await loadPanel({ force: true })
      if (!ownsTool()) return;
    if (selectedThreadId) {
      threadRetryAfterRef.current[selectedThreadId] = 0
      await loadThreadMessages(selectedThreadId, { force: true })
      if (!ownsTool()) return;
    }
    setRefreshing(false)
  }

  const openThreadForRecipient = useCallback(async (recipient: MeetingDirectMessageParticipant) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!meetingId || !panelToken || openingIdentityKey) return
    const existingThread = threadByRecipientKey.get(recipient.identity_key)
    if (existingThread) {
      setSelectedThreadId(existingThread.id)
      return
    }

    setOpeningIdentityKey(recipient.identity_key)
    setError("")
    try {
      const thread = await meetings.createMeetingDirectMessageThread(meetingId, panelToken, {
        recipient_identity_key: recipient.identity_key,
      })
      if (!ownsTool()) return;
      setThreads((prev) => upsertThread(prev, thread))
      setSelectedThreadId(thread.id)
    } catch (err: any) {
      if (!ownsTool()) return;
      setError(err?.response?.data?.detail || err?.message || "Unable to open this conversation.")
    } finally {
      if (!ownsTool()) return;
      setOpeningIdentityKey(null)
    }
  }, [meetingId, openingIdentityKey, panelToken, threadByRecipientKey, captureTool])

  useEffect(() => {
    if (!pendingOpenCommand || !hasLoadedPanelRef.current || openingIdentityKey) return
    const commandKey = getOpenCommandKey(pendingOpenCommand)
    const thread = pendingOpenCommand.threadId
      ? threads.find((candidate) => candidate.id === pendingOpenCommand.threadId)
      : null
    if (thread) {
      setSelectedThreadId(thread.id)
      setPendingOpenCommand(null)
      openCommandRefreshKeyRef.current = ""
      return
    }
    const recipientIdentityKey = pendingOpenCommand.recipientIdentityKey
    if (!recipientIdentityKey) return
    const recipient = (
      recipients.find((candidate) => candidate.identity_key === recipientIdentityKey) ||
      threads.find((candidate) => candidate.recipient.identity_key === recipientIdentityKey)?.recipient ||
      null
    )
    if (!recipient) {
      if (openCommandRefreshKeyRef.current !== commandKey) {
        openCommandRefreshKeyRef.current = commandKey
        void loadPanel({ silent: true, force: true })
        return
      }
      setPendingOpenCommand(null)
      return
    }
    setPendingOpenCommand(null)
    openCommandRefreshKeyRef.current = ""
    void openThreadForRecipient(recipient)
  }, [loadPanel, meetingId, openingIdentityKey, openThreadForRecipient, pendingOpenCommand, recipients, selectedThreadId, threads])

  const sendMessage = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    const ownsDraft = embed.draftState?.captureOwner?.() || ownsTool
    if (!meetingId || !panelToken || !selectedThreadId || sending || !body.trim()) return
    setSendingThreads((previous) => ({ ...previous, [selectedThreadId]: true }))
    setError("")
    try {
      const sent = await meetings.sendMeetingDirectMessage(meetingId, panelToken, selectedThreadId, { body: body.trim() })
      if (!ownsDraft()) return;
      if (ownsTool()) {
      if (selectedThreadIdRef.current === selectedThreadId) {
        setMessages((prev) => [...prev, sent])
      }
      messagesByThreadIdRef.current = {
        ...messagesByThreadIdRef.current,
        [selectedThreadId]: [...(messagesByThreadIdRef.current[selectedThreadId] || messages), sent],
      }
      setThreads((prev) => sortThreads(prev.map((thread) => (
        thread.id === selectedThreadId
          ? { ...thread, last_message: sent, unread_count: 0, updated_at: sent.created_at }
          : thread
      ))))
      }
      // Only clear the exact draft sent; typing or switching threads during the
      // request must not erase newer text or another recipient's draft.
      setDrafts((prev) => {
        if (prev[selectedThreadId] !== body) return prev
        const next = { ...prev }
        delete next[selectedThreadId]
        return next
      })
      if (selectedThread) {
        embed.onMessageSent?.({ threadId: selectedThreadId, recipientIdentityKey: selectedThread.recipient.identity_key, senderIdentityKey: identityKey })
      }
    } catch (err: any) {
      if (!ownsTool()) return;
      setError(err?.response?.data?.detail || err?.message || "Unable to send this message.")
    } finally {
      if (!ownsDraft()) return;
      setSendingThreads((previous) => {
        const next = { ...previous }
        delete next[selectedThreadId]
        return next
      })
    }
  }

  const closeConversation = () => {
    setSelectedThreadId(null)
  }

  const renderAvatar = (recipient: MeetingDirectMessageParticipant) => (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/10 text-slate-300">
      {recipient.avatar_url ? (
        <img src={recipient.avatar_url} alt="" className="h-full w-full object-cover" />
      ) : (
        <UserRound className="h-[18px] w-[18px]" />
      )}
    </span>
  )

  return (
    <div
      data-testid="meeting-messages-panel"
      data-theme="daily-dark"
      data-embedded="true"
      className="absolute inset-0 flex min-h-0 flex-col overflow-hidden bg-[#101620] text-slate-100"
    >
      {selectedThread && (
      <header className="border-b border-white/10 py-3">
        {selectedThread ? (
          <div className="flex items-center gap-2 px-3">
            <Button
              type="button"
              size="auto"
              variant="ghost"
              aria-label="Back to message list"
              onClick={closeConversation}
              className="h-8 min-h-8 w-8 shrink-0 rounded-md p-0 text-slate-300 hover:bg-white/10 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {renderAvatar(selectedThread.recipient)}
            <div className="min-w-0">
              <h1 className="truncate text-sm font-semibold leading-tight text-slate-50">{selectedThread.recipient.display_name}</h1>
              <p className="mt-0.5 text-xs leading-4 text-slate-400">
                {selectedThread.recipient.is_guest ? "Guest" : "Participant"}
              </p>
            </div>
            <Button
              type="button"
              size="auto"
              variant="ghost"
              aria-label="Refresh messages"
              title="Refresh messages"
              onClick={refreshPanel}
              disabled={refreshing}
              className="ml-auto h-7 min-h-7 w-7 shrink-0 rounded-md p-0 text-slate-300 hover:bg-white/10 hover:text-white disabled:text-slate-600"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/10 text-slate-200">
              <MessageSquare className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-sm font-semibold leading-tight text-slate-50">Messages</h1>
              <p className="mt-0.5 text-xs leading-4 text-slate-400">
                Private direct messages with people currently in this call.
              </p>
            </div>
            <Button
              type="button"
              size="auto"
              variant="ghost"
              aria-label="Refresh messages"
              title="Refresh messages"
              onClick={refreshPanel}
              disabled={refreshing}
              className="ml-auto h-7 min-h-7 w-7 shrink-0 rounded-md p-0 text-slate-300 hover:bg-white/10 hover:text-white disabled:text-slate-600"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
        )}
      </header>
      )}

      {selectedThread ? (
        <main data-testid="meeting-messages-conversation-view" className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-3">
            {messagesLoading ? (
              <p className="text-xs text-slate-500">Loading conversation...</p>
            ) : messages.length === 0 ? (
              null
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.is_own ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={`meeting-message-bubble-${message.id}`}
                    className={`min-w-0 max-w-[91%] rounded-[7px] px-3 py-2 text-sm leading-5 ${
                    message.is_own ? "bg-[#20d6ad] text-[#06151a]" : "bg-[#202d3d] text-white"
                  }`}>
                    <p
                      data-testid={`meeting-message-body-${message.id}`}
                      className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
                    >
                      {linkifyText(message.body)}
                    </p>
                    <p className={`mt-0.5 text-[10px] ${message.is_own ? "text-[#064234]/70" : "text-slate-400"}`}>
                      {formatMessageTime(message.created_at)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="border-t border-white/10 bg-[#101620]/95 px-3 py-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={body}
                rows={1}
                onChange={(event) => {
                  const value = event.target.value
                  if (selectedThreadId) setDrafts((prev) => ({ ...prev, [selectedThreadId]: value }))
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault()
                    sendMessage()
                  }
                }}
                placeholder="Type a direct message..."
                className="h-9 min-h-9 max-h-24 resize-none overflow-hidden rounded-md border-white/10 bg-[#17202c] px-3 py-2 text-sm leading-5 text-slate-100 placeholder:text-slate-500 focus-visible:ring-sky-400/40"
              />
              <Button
                type="button"
                size="auto"
                aria-label={sending ? "Sending message" : "Send message"}
                title={sending ? "Sending..." : "Send message"}
                onClick={sendMessage}
                disabled={sending || !body.trim()}
                className="h-9 min-h-9 w-9 shrink-0 rounded-md bg-sky-500 p-0 text-white hover:bg-sky-400 disabled:bg-white/10 disabled:text-slate-500"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </main>
      ) : (
        <main data-testid="meeting-messages-list-view" className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {loading ? (
            <p className="px-1 py-2 text-xs text-slate-500">Loading...</p>
          ) : recipientRows.length === 0 ? (
            <p className="rounded-md border border-white/10 bg-white/5 px-3 py-3 text-xs leading-5 text-slate-400">
              No one else is available to message yet.
            </p>
          ) : (
            <div className="space-y-1">
              {recipientRows.map(({ recipient, thread }) => {
                const unreadCount = thread?.unread_count || 0
                const preview = thread?.last_message?.body || "No messages yet"
                return (
                  <button
                    key={recipient.identity_key}
                    type="button"
                    onClick={() => openThreadForRecipient(recipient)}
                    disabled={openingIdentityKey === recipient.identity_key}
                    className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left text-slate-200 hover:bg-white/10 disabled:opacity-60"
                  >
                    {renderAvatar(recipient)}
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium">{recipient.display_name}</span>
                        {recipient.is_guest && (
                          <span className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">
                            Guest
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">{preview}</span>
                    </span>
                    {unreadCount > 0 && (
                      <span
                        aria-label={`${unreadCount} unread messages from ${recipient.display_name}`}
                        className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-sky-500 px-1.5 text-[11px] font-semibold text-white"
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </main>
      )}

      {error && (
        <p className="mx-3 mb-3 rounded-md border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs font-medium text-red-200">
          {error}
        </p>
      )}
    </div>
  )
}

// A different meeting or participant credential starts a fresh in-memory session.
// Do not persist private drafts in browser storage across calls or sign-ins.
export default function MeetingMessagesPanel({ embed }: { embed: MessagesEmbedContext }) {
  const owner = useToolOwnership(`${embed.meetingId}:${embed.ownershipKey ?? embed.panelToken}`)
  return <MeetingMessagesSession key={owner.key} embed={embed} />
}
